import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, X, ArrowRight, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, FeatureGrid, FAQAccordion } from "@/components/PageBlocks";
import PassportListForm from "@/components/PassportListForm";
import StickyReserve from "@/components/StickyReserve";
import { setUiFlag } from "@/lib/uiState";
import { initWeTravel, wetravelState, wetravelFallback } from "@/lib/wetravel";
import { useSeo } from "@/hooks/useSeo";
import { track } from "@/lib/analytics";
import {
  THAILAND_BOOKING_URL, THAILAND_BROCHURE_URL, THAILAND_FLAGS, TH, EXPERIENCE_CARDS, ITINERARY,
  INCLUDED, NOT_INCLUDED, ROOM_OPTIONS, OPTIONAL_EXPERIENCES, THAILAND_FAQ, PRICING, HOTELS,
  GALLERY, GALLERY_CREDITS, CANCELLATION_POLICY, TRAVEL_PROTECTION, ENTRY_GUIDANCE, WETRAVEL_EMBED,
} from "@/lib/thailand";
import ogMeta from "@/lib/thailand-og.json";

function Reserve({ label = "Reserve Your Spot — $299 Deposit", primary = true, testid = "reserve-cta", location = "mid_page", officialId = false }) {
  // The official handler stops propagation (target phase), so analytics + the
  // loader-failed fallback run in CAPTURE phase (onClickCapture fires first).
  // Child icon is pointer-events-none because the official handler requires the
  // click target to be the button itself.
  const onCapture = () => {
    track("reserve_your_spot_click", { source_page: "Thailand2027", cta_location: location });
    if (wetravelState() === "failed") wetravelFallback();
  };
  const base = "wtrvl-checkout_button inline-flex items-center justify-center gap-3 px-9 py-4 font-body text-xs uppercase tracking-[0.22em] transition-colors duration-300";
  return (
    <button type="button" data-testid={testid} onClickCapture={onCapture}
      id={officialId ? "wetravel_button_widget" : undefined}
      data-env={WETRAVEL_EMBED.env} data-version={WETRAVEL_EMBED.version} data-uid={WETRAVEL_EMBED.uid}
      data-uuid={WETRAVEL_EMBED.uuid} href={WETRAVEL_EMBED.checkoutUrl}
      className={primary ? `${base} border border-gold bg-gold text-black hover:bg-transparent hover:text-gold` : `${base} border border-white/40 text-ink hover:border-gold hover:text-gold`}>
      {label} <ArrowRight size={15} className="pointer-events-none" />
    </button>
  );
}

function scrollTo(id) { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }

// Restrained gallery lightbox: Esc/arrows on desktop, swipe on touch, focus returns to
// the triggering thumbnail, background scroll (native + Lenis) locked while open.
function GalleryLightbox({ items, index, onClose, onNav }) {
  const item = items[index];
  const closeRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const touchX = useRef(null);
  const handlers = useRef({ onClose, onNav });
  handlers.current = { onClose, onNav };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    setUiFlag("overlay", true);
    if (window.__lenis) window.__lenis.stop();
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); handlers.current.onClose(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); handlers.current.onNav(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); handlers.current.onNav(-1); }
      else if (e.key === "Tab") { // keep Tab cycling the three controls without trapping focus oddly
        const ctrls = [closeRef.current, prevRef.current, nextRef.current].filter(Boolean);
        const i = ctrls.indexOf(document.activeElement);
        e.preventDefault();
        ctrls[(i + (e.shiftKey ? -1 : 1) + ctrls.length) % ctrls.length]?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      setUiFlag("overlay", false);
      if (window.__lenis) window.__lenis.start();
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const chipCls = item.tagKind === "included" ? "border-gold/60 text-gold" : item.tagKind === "optional" ? "border-white/40 text-white/70" : "border-white/20 text-white/55";
  const navBtn = "absolute top-1/2 -translate-y-1/2 w-11 h-11 border border-white/25 text-white/80 hover:text-gold hover:border-gold/60 flex items-center justify-center transition-colors duration-300";

  return (
    <div role="dialog" aria-modal="true" aria-label={`Gallery image viewer: ${item.caption}`} data-testid="gallery-lightbox"
      className="fixed inset-0 z-[80] bg-black/95 flex flex-col items-center justify-center p-4 sm:p-10"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) onNav(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}>
      <button ref={closeRef} type="button" onClick={onClose} aria-label="Close gallery viewer" data-testid="lightbox-close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 border border-white/25 text-white/80 hover:text-gold hover:border-gold/60 flex items-center justify-center transition-colors duration-300 z-10">
        <X size={18} />
      </button>
      <button ref={prevRef} type="button" onClick={() => onNav(-1)} aria-label="Previous image" data-testid="lightbox-prev" className={`${navBtn} left-2 sm:left-6`}>
        <ChevronLeft size={20} />
      </button>
      <button ref={nextRef} type="button" onClick={() => onNav(1)} aria-label="Next image" data-testid="lightbox-next" className={`${navBtn} right-2 sm:right-6`}>
        <ChevronRight size={20} />
      </button>
      <picture>
        <source srcSet={`${item.img}.webp`} type="image/webp" />
        <img src={`${item.img}.jpg`} alt={item.alt} draggable={false} data-testid="lightbox-img"
          className="max-h-[66vh] sm:max-h-[74vh] max-w-full w-auto h-auto object-contain border border-white/10" />
      </picture>
      <div className="flex items-baseline gap-3 mt-5 max-w-[90vw]">
        <span data-testid="lightbox-tag" className={`shrink-0 border px-2.5 py-1 font-body text-[9px] uppercase tracking-[0.18em] ${chipCls}`}>{item.tag}</span>
        <span data-testid="lightbox-caption" className="font-body text-white/70 text-xs font-light">{item.caption}</span>
        <span className="font-body text-white/40 text-[11px] shrink-0">{index + 1} / {items.length}</span>
      </div>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  return (
    <section ref={ref} data-testid="thailand-hero" className="relative min-h-screen w-full overflow-hidden flex items-end">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={TH.hero} alt="Wat Arun temple glowing over the Chao Phraya River at dusk, Bangkok" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/35 to-black/50" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 12% 95%, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.4) 42%, transparent 72%)" }} />
      <div className="relative z-10 max-w-[1500px] mx-auto w-full px-6 md:px-12 pt-32 pb-20 md:pb-24">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="font-body text-[11px] md:text-xs uppercase tracking-[0.4em] text-gold mb-4">
          Inforno Travel presents · The Passport Experience™
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 1 }}
          className="font-serif font-light tracking-tighter leading-[0.88] text-5xl sm:text-7xl lg:text-[7rem] text-ink">
          Thailand <span className="text-gold">2027</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 1 }}
          className="font-body text-sm md:text-base uppercase tracking-[0.3em] text-white/80 mt-5">
          Bangkok • Phuket &nbsp;|&nbsp; {PRICING.dates}
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 1 }}
          className="font-serif italic text-xl md:text-3xl font-light text-ink/90 mt-8 max-w-2xl leading-snug">
          Your passport gets you there. The Passport Experience takes you beyond.
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 1 }}
          className="font-body text-[11px] uppercase tracking-[0.3em] text-white/60 mt-6">
          7 Nights • 5-Star Stays • Curated Experiences
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 1 }}
          className="mt-8 flex flex-wrap items-end gap-x-10 gap-y-4">
          <div>
            <p className="font-body text-[10px] uppercase tracking-[0.25em] text-gold">Starting at</p>
            <p className="font-serif text-4xl font-light">{PRICING.fromPrice} <span className="text-base text-white/60">/ person, double occ.</span></p>
            <p className="font-body text-[11px] text-white/55 mt-1">{PRICING.taxesNote}</p>
          </div>
          <div>
            <p className="font-body text-[10px] uppercase tracking-[0.25em] text-gold">Deposit</p>
            <p className="font-serif text-4xl font-light">{PRICING.deposit} <span className="text-base text-white/60">per person</span></p>
          </div>
          <p className="font-body text-xs text-white/60">{PRICING.paymentPlans}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 1 }}
          className="mt-8 flex flex-col sm:flex-row gap-4">
          <Reserve label="Reserve Your Spot — $299 Deposit" testid="reserve-hero" location="hero" officialId />
          <button data-testid="explore-experience" onClick={() => { track("explore_experience_click"); scrollTo("intro"); }}
            className="inline-flex items-center justify-center gap-3 border border-white/40 text-ink px-9 py-4 font-body text-xs uppercase tracking-[0.22em] hover:border-gold hover:text-gold transition-colors duration-300">
            Explore the Experience
          </button>
        </motion.div>
        <p className="font-body text-[11px] text-white/60 mt-4 tracking-wide">{PRICING.deposit} per person deposit · {PRICING.paymentPlans}</p>
        <p className="font-body text-xs text-white/65 mt-3 max-w-xl">{PRICING.airfareNote}</p>
      </div>
    </section>
  );
}

function Section({ id, className = "bg-noir", children }) {
  return <section id={id} className={`py-24 md:py-32 px-6 md:px-12 ${className}`}><div className="max-w-[1500px] mx-auto">{children}</div></section>;
}

export default function Thailand2027() {
  useEffect(() => { window.scrollTo(0, 0); track("thailand_page_view"); initWeTravel(); }, []);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const closeLightbox = () => {
    const slug = lightboxIdx != null ? GALLERY[lightboxIdx].slug : null;
    setLightboxIdx(null);
    if (slug) requestAnimationFrame(() => document.querySelector(`[data-testid="gallery-open-${slug}"]`)?.focus());
  };
  const navLightbox = (d) => setLightboxIdx((i) => (i + d + GALLERY.length) % GALLERY.length);
  useSeo({
    title: ogMeta.title, description: ogMeta.description, canonical: ogMeta.url, image: ogMeta.image,
    jsonLd: {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: THAILAND_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  });

  return (
    <div className="bg-noir">
      <Navbar />
      <Hero />

      {/* Section 2 — Introduction */}
      <Section id="intro">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7"><SectionHeading overline="The Journey" title={<>Two Sides of Thailand.<br /><span className="italic text-white/60">One Extraordinary Experience.</span></>} /></div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <p className="font-body text-white/65 text-base leading-relaxed font-light">From Bangkok's golden temples and electric city energy to Phuket's turquoise waters and island beauty, The Passport Experience™: Thailand 2027 brings together culture, adventure, relaxation and connection in one thoughtfully curated journey.</p>
              <p className="font-body text-white/65 text-base leading-relaxed font-light mt-4">This is more than a vacation. It's seven unforgettable nights experiencing Thailand with a community of travelers ready to see the world differently.</p>
              {THAILAND_FLAGS.brochure && THAILAND_BROCHURE_URL && (
                <a href={THAILAND_BROCHURE_URL} target="_blank" rel="noopener noreferrer" data-testid="thailand-brochure-intro"
                  onClick={() => track("thailand_brochure_click", { source_page: "Thailand2027", cta_location: "intro", file_type: "pdf" })}
                  className="mt-7 inline-flex items-center gap-2 text-gold font-body text-[11px] uppercase tracking-[0.22em] link-underline">
                  View Trip Brochure
                </a>
              )}
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Section 3 — The Passport Experience */}
      <Section id="passport" className="bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-3xl">
          <SectionHeading overline="The Passport Experience™" title={<>This Isn't Just Thailand.<br /><span className="italic text-white/60">It's The Passport Experience™.</span></>} />
          <Reveal delay={0.1}>
            <p className="font-body text-white/65 text-base leading-relaxed font-light mt-6">The Passport Experience™ is Inforno Travel's hosted international travel series, created for travelers who want more than simply arriving at a destination.</p>
            <p className="font-body text-white/65 text-base leading-relaxed font-light mt-4">We combine thoughtfully curated experiences, premium accommodations, memorable moments and the energy of traveling together into a journey designed to take you beyond the ordinary vacation.</p>
            <p className="font-serif italic text-2xl md:text-3xl font-light text-gold mt-10 leading-snug">Your passport gets you there.<br />The Passport Experience takes you beyond.</p>
            <p className="font-serif text-xl font-light text-white/70 mt-8">Where will your passport take you next?</p>
          </Reveal>
        </div>
      </Section>

      {/* Section 4 — Bangkok + Phuket split */}
      <section className="bg-noir">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {[
            { city: "Bangkok", dates: "June 19–22, 2027", nights: "3 Nights", img: TH.bangkokTemple, items: ["Golden temples", "Historic landmarks", "Markets", "River experiences", "Culture", "City energy"] },
            { city: "Phuket", dates: "June 22–26, 2027", nights: "4 Nights", img: TH.phuket, items: ["Turquoise waters", "Island adventures", "Phi Phi Islands", "Elephant sanctuary", "Beach time", "Relaxation"] },
          ].map((c) => (
            <div key={c.city} className="relative min-h-[70vh] flex items-end overflow-hidden group">
              <img src={c.img} alt={c.city} loading="lazy" className="absolute inset-0 w-full h-full object-cover brightness-[0.5] group-hover:brightness-[0.65] group-hover:scale-[1.04] transition-all duration-[1200ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
              <div className="relative z-10 p-8 md:p-12">
                <h3 className="font-serif text-4xl md:text-5xl font-light mb-2">{c.city}</h3>
                <p className="font-body text-[11px] uppercase tracking-[0.25em] text-gold mb-6">{c.dates} · {c.nights}</p>
                <ul className="space-y-2">
                  {c.items.map((i) => <li key={i} className="font-body text-white/80 text-sm flex items-center gap-3"><span className="w-1.5 h-1.5 bg-gold rounded-full" />{i}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 text-center">
          <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-6">Bangkok 3 Nights · Phuket 4 Nights · Total 7 Nights</p>
          <p className="font-serif text-2xl md:text-4xl font-light text-white/80 leading-snug">Golden temples to turquoise waters.<br />City energy to island paradise.<br /><span className="text-gold italic">One unforgettable Thailand experience.</span></p>
        </div>
      </section>

      {/* Section 4b — Where You'll Stay (locked accommodations; property photography = owner/vendor asset pending) */}
      <Section id="stay">
        <div className="mb-14"><SectionHeading overline="Where You'll Stay" title="5-Star Stays, Named and Confirmed" sub="Two hand-selected five-star properties — city energy in Bangkok, beachside calm in Phuket. Daily buffet breakfast and airport transfers are included." /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {HOTELS.map((h) => (
            <div key={h.name} data-testid={`hotel-${h.city.toLowerCase()}`} className="bg-noir">
              <div className={`relative overflow-hidden ${h.aspect}`}>
                <picture>
                  {h.imgMobile && <source media="(max-width: 768px)" srcSet={`${h.imgMobile}.webp`} type="image/webp" />}
                  {h.imgMobile && <source media="(max-width: 768px)" srcSet={`${h.imgMobile}.jpg`} />}
                  <source srcSet={`${h.img}.webp`} type="image/webp" />
                  <img src={`${h.img}.jpg`} alt={h.alt} width="1365" height="768" loading="lazy" data-testid={`hotel-img-${h.city.toLowerCase()}`} className="w-full h-full object-cover" />
                </picture>
              </div>
              <div className="p-8 md:p-10">
                <h3 className="font-serif text-2xl md:text-3xl font-light">{h.name}</h3>
                <p className="font-body text-[11px] uppercase tracking-[0.25em] text-gold mt-3">{h.city} · {h.dates} · {h.nights}</p>
                <p className="font-body text-white/65 text-sm mt-4 leading-relaxed font-light">{h.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <Reveal><p className="font-body text-white/55 text-xs mt-6 font-light">Your confirmed 5-star properties · Total: 7 Nights · Daily buffet breakfast and airport transfers included.</p></Reveal>
      </Section>

      {/* Section 5 — Experience Highlights (6 in grid = 2 clean rows; 7th as full-width feature) */}
      <Section id="highlights" className="bg-[#0a0a0a] border-y border-white/10">
        <div className="mb-14"><SectionHeading overline="Experience Highlights" title="What's woven into your week" /></div>
        <FeatureGrid items={EXPERIENCE_CARDS.slice(0, 6)} />
        <Reveal>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 border border-white/10 overflow-hidden">
            <div className="relative min-h-[220px] md:min-h-[300px]">
              <img src={EXPERIENCE_CARDS[6].img} alt={EXPERIENCE_CARDS[6].title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/20" />
            </div>
            <div className="p-8 md:p-12 bg-noir flex flex-col justify-center">
              <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-4">Built-In Freedom</p>
              <h3 className="font-serif text-3xl md:text-4xl font-light mb-3">{EXPERIENCE_CARDS[6].title}</h3>
              <p className="font-body text-white/65 leading-relaxed font-light">{EXPERIENCE_CARDS[6].desc}</p>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* Section 6 — Itinerary */}
      <Section id="itinerary">
        <div className="mb-14"><SectionHeading overline="Day by Day" title="Your Thailand itinerary" /></div>
        <div className="border-t border-white/10">
          {ITINERARY.map((it, i) => (
            <Reveal key={it.d} delay={(i % 4) * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-7 border-b border-white/10 items-baseline">
                <div className="md:col-span-2"><span className="font-body text-[11px] uppercase tracking-[0.25em] text-gold">{it.d}</span></div>
                <h3 className="md:col-span-4 font-serif text-2xl font-light">{it.t}</h3>
                <div className="md:col-span-6">
                  <p className="font-body text-white/65 text-sm md:text-base font-light">{it.desc}</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-3">
                    {it.meals && <span data-testid={`day-meals-${i + 1}`} className="border border-gold/50 text-gold px-2.5 py-1 font-body text-[9px] uppercase tracking-[0.18em]">Meals: {it.meals}</span>}
                    <button type="button" onClick={() => setOpenDay(openDay === i ? null : i)} aria-expanded={openDay === i} data-testid={`day-toggle-${i + 1}`}
                      className="flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.2em] link-underline">
                      {openDay === i ? "Hide day details" : "Day details"}
                      <ChevronDown size={13} className={`transition-transform duration-300 ${openDay === i ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {openDay === i && <p data-testid={`day-detail-${i + 1}`} className="font-body text-white/60 text-sm mt-3 leading-relaxed font-light">{it.detail}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Section 7 — Included / Not Included */}
      <Section id="whats-included" className="bg-[#0a0a0a] border-y border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <Reveal>
            <h3 className="font-serif text-3xl font-light mb-8 flex items-center gap-3"><Check className="text-gold" size={22} /> What's Included</h3>
            <ul className="space-y-3">{INCLUDED.map((i) => <li key={i} className="font-body text-white/75 text-sm flex items-start gap-3"><Check size={15} className="text-gold mt-1 shrink-0" />{i}</li>)}</ul>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-serif text-3xl font-light mb-8 flex items-center gap-3"><X className="text-white/40" size={22} /> Not Included</h3>
            <ul className="space-y-3">{NOT_INCLUDED.map((i) => <li key={i} className="font-body text-white/75 text-sm flex items-start gap-3"><X size={15} className="text-white/40 mt-1 shrink-0" />{i}</li>)}</ul>
            <div className="mt-8 border border-gold/40 bg-gold/[0.06] p-5">
              <p className="font-body text-sm text-gold uppercase tracking-[0.15em]">Please note</p>
              <p className="font-body text-white/80 text-sm mt-2 font-light">International airfare is not included. The Bangkok-to-Phuket group flight is available as an optional {PRICING.groupFlight}-per-person add-on.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Section 8 — Pricing */}
      <Section id="pricing">
        <div className="text-center max-w-3xl mx-auto">
          <SectionHeading center overline="Pricing" title="Your Thailand Experience Starts at $1,999" />
          <Reveal delay={0.1}>
            <p className="font-body text-white/70 mt-6">{PRICING.fromPrice} per person · Double occupancy</p>
            <p className="font-body text-white/70 mt-1">Reserve with {PRICING.deposit} per person · {PRICING.paymentPlans}</p>
            <p className="font-body text-white/60 text-sm mt-1">{PRICING.taxesNote}.</p>
            <p className="font-body text-white/60 text-sm mt-1">Final payment due {PRICING.finalPaymentDate}.</p>
            <div className="mt-8 flex justify-center"><Reserve label="Reserve Your Spot — $299 Deposit" testid="reserve-pricing" location="mid_page" /></div>
            {THAILAND_FLAGS.brochure && THAILAND_BROCHURE_URL && (
              <div className="mt-5 flex justify-center">
                <a href={THAILAND_BROCHURE_URL} target="_blank" rel="noopener noreferrer" data-testid="thailand-brochure"
                  onClick={() => track("thailand_brochure_click", { source_page: "Thailand2027", cta_location: "pricing", file_type: "pdf" })}
                  className="inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.2em] link-underline">
                  View Trip Brochure
                </a>
              </div>
            )}
            <p className="font-body text-white/60 text-xs mt-6 max-w-xl mx-auto">{PRICING.airfareNote} Payment-processing fees may apply depending on payment method and will be displayed during checkout.</p>
          </Reveal>
        </div>
      </Section>

      {/* Section 9 — Room Options */}
      <Section id="rooms" className="bg-[#0a0a0a] border-y border-white/10">
        <div className="mb-14"><SectionHeading overline="Room Options" title="Choose the Room Style That Works for You" /></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {ROOM_OPTIONS.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.08}>
              <div className="bg-noir p-8 md:p-10 h-full">
                <h3 className="font-serif text-2xl font-light">{r.title}</h3>
                <p className="font-body text-white/60 text-sm mt-2">{r.detail}</p>
                <p className="font-serif text-3xl font-light text-gold mt-6">{r.price}</p>
                <p className="font-body text-white/60 text-xs uppercase tracking-[0.2em] mt-1">{r.per}</p>
                {r.note && <p className="font-body text-white/65 text-xs mt-5 leading-relaxed font-light">{r.note}</p>}
              </div>
            </Reveal>
          ))}
        </div>
        {THAILAND_FLAGS.roommateSeparatePayment && (
          <Reveal><div className="mt-8 border border-gold/30 p-6" data-testid="roommate-block">
            <p className="font-serif text-xl font-light">Traveling with a roommate but paying separately?</p>
            <p className="font-body text-white/60 text-sm mt-2 font-light">Separate-payment roommate reservations are supported through the trip's booking system. Each traveler can maintain their own reservation and payment account while sharing the same room.</p>
          </div></Reveal>
        )}
        <Reveal><p className="font-body text-white/50 text-xs mt-6 font-light max-w-3xl">Included room category at both confirmed properties: Standard King or Standard Twin. Bedding preferences are requests, subject to hotel availability and final confirmation. Prefer a higher category? You may request an upgrade directly with the hotel at check-in, subject to availability — any additional cost is paid directly to the hotel.</p></Reveal>
      </Section>

      {/* Section 9b — Cancellation & Refund Policy (summarized from official booking terms) */}
      <Section id="cancellation">
        <div className="mb-14"><SectionHeading overline="Cancellation & Refund Policy" title="Know the Terms Before You Reserve" sub={CANCELLATION_POLICY.intro} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="cancellation-policy">
          {CANCELLATION_POLICY.tiers.map((t, i) => (
            <Reveal key={t.when} delay={i * 0.06}>
              <div className="bg-noir p-7 md:p-8 h-full">
                <p className="font-body text-[10px] uppercase tracking-[0.2em] text-gold">{t.when}</p>
                <p className="font-serif text-xl font-light mt-4">{t.result}</p>
                <p className="font-body text-white/55 text-xs mt-3 font-light">{t.deposit}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="border border-white/10 p-7 md:p-8 mt-px" data-testid="cancellation-credit">
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-gold">Your Travel Credit</p>
            <p className="font-body text-white/65 text-sm mt-3 leading-relaxed font-light max-w-4xl">{CANCELLATION_POLICY.credit}</p>
            <p className="font-body text-white/65 text-sm mt-4 leading-relaxed font-light max-w-4xl"><span className="text-white/85">Optional group flight:</span> {CANCELLATION_POLICY.flightNote.replace("Optional Bangkok–Phuket group flight: ", "")}</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="font-body text-white/50 text-xs mt-6 font-light max-w-3xl">{CANCELLATION_POLICY.authority}{" "}
            <a href={THAILAND_BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="cancellation-terms-link" className="text-gold link-underline">Review the official booking terms</a>
          </p>
        </Reveal>
      </Section>

      {/* Section 9c — Good to Know: Travel Protection + Passports & Entry */}
      <Section id="good-to-know" className="bg-[#0a0a0a] border-y border-white/10">
        <div className="mb-14"><SectionHeading overline="Good to Know" title="Travel Protection & Entry Requirements" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {[TRAVEL_PROTECTION, ENTRY_GUIDANCE].map((p) => (
            <div key={p.title} className="bg-noir p-8 md:p-10" data-testid={p.title === "Travel Protection" ? "travel-protection" : "entry-guidance"}>
              <h3 className="font-serif text-2xl md:text-3xl font-light mb-6">{p.title}</h3>
              <ul className="space-y-3">
                {p.points.map((pt) => <li key={pt} className="font-body text-white/70 text-sm flex items-start gap-3 font-light"><Check size={15} className="text-gold mt-1 shrink-0" />{pt}</li>)}
              </ul>
              {p.note && <p className="font-body text-white/50 text-xs mt-6 font-light">{p.note}</p>}
            </div>
          ))}
        </div>
      </Section>

      {/* Section 10 — Who this is for */}
      <Section id="who-for">
        <div className="text-center max-w-3xl mx-auto">
          <SectionHeading center overline="Who It's For" title="Come With Your Person—or Come Meet Your People" />
          <Reveal delay={0.1}>
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mt-6">Couples • Friends • Solo Travelers • Families Welcome</p>
            <p className="font-body text-white/65 mt-6 leading-relaxed font-light">The Passport Experience™ is designed for travelers who want to experience the destination without having to plan every detail themselves. Come with someone you know—or join a community of travelers ready to experience Thailand together.</p>
          </Reveal>
        </div>
      </Section>

      {/* Section 11 — Optional Experiences (unified card hierarchy; only approved/active render) */}
      {OPTIONAL_EXPERIENCES.some((o) => THAILAND_FLAGS[o.key]) && (
        <Section id="optional" className="bg-[#0a0a0a] border-y border-white/10">
          <div className="mb-14"><SectionHeading overline="Optional Add-On Experiences • Additional Cost" title="Enhance Your Passport Experience" sub="Your core Passport Experience already includes an extensive curated itinerary. The experiences below are optional add-ons — not included in the base package — and are subject to availability and confirmation." /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {OPTIONAL_EXPERIENCES.filter((o) => THAILAND_FLAGS[o.key]).map((o) => (
              <div key={o.key} data-testid={o.key === "optional_yona" ? "optional-yona-feature" : `optional-${o.key}`} className="bg-noir h-full flex flex-col">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <picture>
                    <source srcSet={`${o.img}.webp`} type="image/webp" />
                    <img src={`${o.img}.jpg`} alt={o.imgAlt} width="1600" height="900" loading="lazy" className="w-full h-full object-cover" />
                  </picture>
                </div>
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <p className="font-body text-[10px] uppercase tracking-[0.25em] text-gold">Optional Add-On · Additional Cost</p>
                  <div className="flex flex-wrap items-center gap-4 mt-4">
                    <h3 className="font-serif text-2xl md:text-3xl font-light">{o.title}</h3>
                    {o.age && <span data-testid={o.key === "optional_yona" ? "optional-age-badge-yona" : "optional-age-badge"} className="shrink-0 border border-gold text-gold px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.2em]">{o.age}</span>}
                  </div>
                  {o.kicker && <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mt-3">{o.kicker}</p>}
                  <p className="font-serif text-3xl font-light text-gold mt-5">{o.price}</p>
                  <p className="font-body text-white/65 text-sm mt-4 leading-relaxed font-light">{o.note}</p>
                  <p className="font-body text-white/50 text-[11px] uppercase tracking-[0.15em] mt-auto pt-6">Subject to availability and confirmation</p>
                </div>
              </div>
            ))}
          </div>
          <Reveal>
            <p className="font-body text-white/50 text-xs mt-6 font-light max-w-3xl">Subject to availability and confirmation. Optional experiences are not included in the {PRICING.fromPrice} base package and are selected and confirmed through the official Thailand 2027 booking flow after reserving your spot.</p>
          </Reveal>
        </Section>
      )}

      {/* Section 12 — Welcome Experience (hidden until approved) */}
      {THAILAND_FLAGS.welcomeExperience && (
        <Section id="welcome" data-testid="welcome-block">
          <div className="max-w-3xl">
            <SectionHeading overline="Welcome" title="Welcome to The Passport Experience™" />
            <Reveal delay={0.1}><p className="font-body text-white/65 mt-6 leading-relaxed font-light">We'll begin our journey together with an Inforno Travel welcome experience in Bangkok—a chance to meet your fellow travelers, connect and officially kick off an unforgettable week in Thailand.</p></Reveal>
          </div>
        </Section>
      )}

      {/* Section 13 — Flight Guidance */}
      <Section id="flights" className="bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-3xl">
          <SectionHeading overline="Flights" title="Before You Book Your Flight" />
          <Reveal delay={0.1}>
            <p className="font-body text-white/65 mt-6 leading-relaxed font-light">International airfare is not included. A coordinated Bangkok-to-Phuket group flight is available as an optional {PRICING.groupFlight}-per-person add-on, including 20 kg of checked baggage and 7 kg of hand baggage, with an additional 10 kg available for $40. Travelers who do not purchase the group-flight option should wait for Inforno Travel's official flight guidance before arranging their own Bangkok-to-Phuket flight.</p>
            <p className="font-body text-white/65 mt-4 leading-relaxed font-light">Guests must arrive in Bangkok (BKK) in time for the start of the program on June 19, 2027, and the trip concludes in Phuket (HKT) on June 26, 2027.</p>
            <div className="mt-6 border border-gold/40 bg-gold/[0.06] p-5">
              <p className="font-body text-gold uppercase tracking-[0.15em] text-sm">Important</p>
              <p className="font-serif text-xl font-light mt-2">Please wait for Inforno Travel's official flight-booking guidance before purchasing airfare.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Section 14 — FAQ */}
      <Section id="faq">
        <div className="max-w-[900px] mx-auto">
          <div className="mb-10"><SectionHeading overline="FAQ" title="Good to know" /></div>
          <FAQAccordion items={THAILAND_FAQ} />
        </div>
      </Section>

      {/* Section 15b — See the Experience gallery (authentic imagery; tags guard included vs atmosphere) */}
      <Section id="gallery">
        <div className="mb-6"><SectionHeading overline="A Glimpse of Thailand 2027" title="See the Experience" sub="Real places, real moments — the stays, the experiences and the energy of The Passport Experience™: Thailand 2027." /></div>
        <Reveal delay={0.05}>
          <p className="font-body text-[11px] uppercase tracking-[0.2em] text-white/45 mb-12">
            <span className="text-gold">Included Experience</span> = part of your package&ensp;·&ensp;Where You'll Stay = your named 5-star properties&ensp;·&ensp;Optional Add-On = additional cost&ensp;·&ensp;Destination Atmosphere / Bangkok Energy = the city and culture around your journey
          </p>
        </Reveal>
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="thailand-gallery">
          {GALLERY.map((g, i) => (
            <Reveal key={g.slug} delay={(i % 3) * 0.06}>
              <figure className="break-inside-avoid mb-5 group">
                <button type="button" onClick={() => setLightboxIdx(i)} aria-label={`View larger: ${g.caption}`} data-testid={`gallery-open-${g.slug}`}
                  className="block w-full text-left overflow-hidden border border-white/10 cursor-zoom-in">
                  <picture>
                    <source srcSet={`${g.img}.webp`} type="image/webp" />
                    <img src={`${g.img}.jpg`} alt={g.alt} width={g.w} height={g.h} loading="lazy"
                      data-testid={`gallery-img-${g.slug}`}
                      className="w-full h-auto group-hover:scale-[1.03] transition-transform duration-[1200ms]" />
                  </picture>
                </button>
                <figcaption className="flex items-baseline gap-3 mt-3">
                  <span data-testid={`gallery-tag-${g.slug}`}
                    className={`shrink-0 border px-2.5 py-1 font-body text-[9px] uppercase tracking-[0.18em] ${g.tagKind === "included" ? "border-gold/60 text-gold" : g.tagKind === "optional" ? "border-white/40 text-white/70" : "border-white/20 text-white/55"}`}>
                    {g.tag}
                  </span>
                  <span className="font-body text-white/60 text-xs font-light">{g.caption}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal><p className="font-body text-white/35 text-[11px] font-light mt-8 max-w-4xl">{GALLERY_CREDITS}</p></Reveal>
      </Section>

      {/* Section 16 — Final CTA */}
      <section className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden">
        <img src={TH.phiphi} alt="" className="absolute inset-0 w-full h-full object-cover brightness-[0.35]" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/60 to-black/40" />
        <div className="relative z-10 max-w-[1500px] mx-auto text-center">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-7xl font-light tracking-tight">Thailand Is Calling.</h2>
            <p className="font-body text-white/75 mt-6 text-lg font-light">Bangkok. Phuket. Seven nights. One Passport Experience.</p>
            <p className="font-serif italic text-2xl text-gold mt-4">Where will your passport take you next?</p>
            <div className="mt-10 flex justify-center"><Reserve label="Reserve Your Spot — $299 Deposit" testid="reserve-final" location="final_cta" /></div>
            <p className="font-body text-[11px] text-white/65 mt-5 tracking-wide">{PRICING.deposit} per person · {PRICING.paymentPlans}</p>
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-white/50 mt-4">Presented by Inforno Travel</p>
          </Reveal>
        </div>
      </section>

      {/* Section 17 — Passport List email capture (end-of-page: every decision section comes first) */}
      <Section id="passport-list" className="bg-[#0a0a0a] border-t border-white/10">
        <PassportListForm sourcePage="Thailand2027" campaignSource="thailand2027_page" />
      </Section>

      <Footer />
      <StickyReserve />
      {lightboxIdx != null && <GalleryLightbox items={GALLERY} index={lightboxIdx} onClose={closeLightbox} onNav={navLightbox} />}
    </div>
  );
}
